import React from 'react';
import {
  AppBar,
  Box,
  Container,
  Divider,
  Toolbar,
  Typography,
  Skeleton,
} from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import classNames from 'classnames';
import { formatUrl } from '../../lib/String';
import { OrganizationSchema } from '../../definitions/schemas';
import Link from '../Link';

const useStyles = makeStyles((theme: Theme) => ({
  appBar: {
    [theme.breakpoints.up('md')]: {
      /**
       * Adding a margin because the content overflow scrollbar pushes
       * the content a few pixels from the right. Since overflow: overlay
       * isn't fully supported (it doesn't work in Safari at all), this is
       * the easiest fix to align the appbar with the content
       */
      paddingRight: theme.spacing(1.75),
    },
  },
  container: {
    // matches the room layout maximum width
    maxWidth: theme.spacing(142.5),
    padding: 0,
  },
  divider: {
    margin: theme.spacing(0.5, 1.5),
    [theme.breakpoints.up('md')]: {
      margin: theme.spacing(0.5, 3),
    },
  },
  header: {
    display: 'flex',
    justifyContent: 'center',
  },
  logo: {
    maxHeight: theme.spacing(5),
    maxWidth: `calc(50% - ${theme.spacing(1.5)})`,
    objectFit: 'scale-down',
    [theme.breakpoints.up('md')]: {
      maxWidth: theme.spacing(22),
    },
  },
  orgLogoWide: {
    maxHeight: theme.spacing(5),
    maxWidth: '100%',
    verticalAlign: 'middle',
    [theme.breakpoints.up('md')]: {
      maxWidth: theme.spacing(22),
    },
  },
  toolbar: {
    [theme.breakpoints.up('lg')]: {
      padding: 0,
    },
  },
}));

export interface RoomAppBarProps {
  centeredLogos?: boolean;
  className?: string;
  clientLogo?: string;
  navigation?: React.ReactNode;
  organization?: OrganizationSchema;
  primaryAction?: React.ReactNode;
  secondaryAction?: React.ReactNode;
}

const RoomAppBar: React.FC<RoomAppBarProps> = (
  props: RoomAppBarProps
): JSX.Element => {
  const classes = useStyles();

  const {
    centeredLogos = false,
    className,
    clientLogo,
    navigation,
    organization,
    primaryAction,
    secondaryAction,
  } = props;

  const renderOrganizationLogo = () => {
    if (!organization) {
      return;
    }

    let logo = (
      <Typography component="h1" variant="h5">
        {organization.name}
      </Typography>
    );

    if (organization.logo) {
      logo = (
        <img
          className={classNames({
            [classes.orgLogoWide]: !!organization.website,
          })}
          src={organization.logo}
          alt={`${organization.name} logo`}
        />
      );
    }

    if (organization.website) {
      return (
        <Link
          className={classes.logo}
          href={formatUrl(organization.website)}
          text={logo}
        />
      );
    }

    return logo;
  };

  const renderLogos = (): JSX.Element => (
    <Box
      alignItems="center"
      display="flex"
      flex={1}
      justifyContent={centeredLogos ? 'center' : 'flex-start'}
      textAlign={centeredLogos ? 'center' : 'left'}
    >
      {!organization ? (
        <Skeleton
          className={classes.logo}
          sx={(theme) => ({ height: theme.spacing(3.75), width: '100%' })}
          variant="rectangular"
        />
      ) : (
        <>
          {renderOrganizationLogo()}
          {clientLogo && (
            <>
              <Divider
                className={classes.divider}
                flexItem
                orientation="vertical"
              />
              <img
                alt="client logo"
                className={classes.logo}
                src={clientLogo}
              />
            </>
          )}
        </>
      )}
    </Box>
  );

  return (
    <AppBar className={classes.appBar} color="transparent" position="static">
      <Container
        className={classNames(classes.container, className)}
        disableGutters
        maxWidth={false}
      >
        <Toolbar
          className={classNames(classes.toolbar, {
            [classes.header]: centeredLogos,
          })}
        >
          {secondaryAction}
          {renderLogos()}
          {primaryAction}
        </Toolbar>
        {navigation}
      </Container>
    </AppBar>
  );
};

export default RoomAppBar;
